import {
  EventData,
  FetchedEvent,
  CreatedEvent,
  CreatedExceptionEvent,
} from 'utils/types/learningSchedule';

export function fixAllDayEventDates(
  startStr: string,
  endStr: string
): { start: string; end: string } {
  const startDate = new Date(startStr);
  const endDate = new Date(endStr);

  const startLocal = `${startDate.getFullYear()}-${pad(
    startDate.getMonth() + 1
  )}-${pad(startDate.getDate())}T00:00:00`;
  const endLocal = `${endDate.getFullYear()}-${pad(
    endDate.getMonth() + 1
  )}-${pad(endDate.getDate())}T00:00:00`;

  return {
    start: startLocal,
    end: endLocal,
  };
}

export function pad(num: number) {
  return num.toString().padStart(2, '0');
}

export function extractDateOnly(dateTimeStr: string): string {
  const date = new Date(dateTimeStr);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`;
}
export const serializeEvents = (events: FetchedEvent[]): EventData[] => {
  const eventsMap = new Map<string, FetchedEvent>();
  events.forEach((event) => {
    eventsMap.set(event.id, event);
  });
  return events.map((event) => {
    let start = event.startTime;
    let end = event.endTime;
    let isAllDay = event.isAllDay;

    if (isAllDay) {
      // Duration check: if less than 24h, treat as timed event
      const startDate = new Date(event.startTime);
      const endDate = new Date(event.endTime);
      if (endDate.getTime() - startDate.getTime() < 24 * 60 * 60 * 1000) {
        start = event.startTime;
        end = event.endTime;
      } else {
        // For all-day events, preserve the original UTC times
        // This ensures consistent display across all timezones
        start = event.startTime;
        end = event.endTime;
      }
    }
    return {
      Id: event.id,
      Subject: event.subject,
      Description: event.description ?? null,
      StartTime: start,
      EndTime: end,
      ProjectId: event?.projectIds || [],
      IsAllDay: event.isAllDay,
      EventType: event?.learningEventType || '',
      RecurrenceRule: event?.parentId
        ? eventsMap.get(event.parentId)?.recurrenceRule
        : event?.recurrenceRule,
      RecurrenceException: event.parentId
        ? eventsMap.get(event.parentId)?.recurrenceException
        : event?.recurrenceException,
      projects: event?.projects,
      facilitators: event?.facilitators,
      FacilitatorId: event?.facilitators?.map((facilitator) => facilitator.id),
      RecurrenceID: event?.parentId || null,
      createdBy: event?.createdBy,
      isOldEvent: event?.isOldEvent,
      holidayCountry: event?.holidayCountry,
    };
  });
};

export const serializeCreatedEvent = (event: CreatedEvent): EventData => {
  return {
    Id: event.id,
    Subject: event.title,
    Description: event.description ?? null,
    StartTime: event.startDateTime,
    EndTime: event.endDateTime,
    ProjectId: event.projects.map((project) => project.id),
    projects: event.projects.map((project) => ({
      id: project.id,
      title: project.title,
    })),
    facilitators: event.facilitators.map((facilitator) => ({
      id: facilitator.id,
      name: facilitator.name,
    })),
    FacilitatorId: event.facilitators.map((facilitator) => facilitator.id),
    IsAllDay: event.isAllDay,
    EventType: event.learningEventType,
    RecurrenceRule: event.recurrencePattern,
    RecurrenceException: event.recurrenceException,
    RecurrenceID: event.parentId || null,
    isOldEvent: event.isOldEvent,
    createdBy: event.createdBy,
  };
};

export const isTheCurrentUserAFacilitator = (
  event: EventData,
  userId: string
) => {
  return event.facilitators?.some((facilitator) => facilitator.id === userId);
};

export const serializeExceptionEvent = (
  event: CreatedExceptionEvent
): EventData => {
  return {
    Id: event.id,
    Subject: event.title,
    Description: event.description ?? null,
    StartTime: event.startDateTime,
    EndTime: event.endDateTime,
    ProjectId: event.projects.map((project) => project.id),
    projects:
      event.projects.length > 0
        ? event.projects.map((project) => ({
            id: project.id,
            title: project.title,
          }))
        : [],
    facilitators: event.facilitators.map((facilitator) => ({
      id: facilitator.id,
      name: facilitator.name,
    })),
    FacilitatorId: event.facilitators.map((facilitator) => facilitator.id),
    IsAllDay: event.isAllDay,
    isOldEvent: event.isOldEvent,
    EventType: event.learningEventType,
    RecurrenceRule: event.recurrencePattern,
    RecurrenceException: '',
    RecurrenceID: event.isException ? event.parent.id : null,
    createdBy: event.createdBy,
  };
};
